import { useMemo } from 'react';
import { assertNever } from '@sas/nova-core/typeguards';
import { BrowserName, getBrowserName } from '../../browser-name';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { useFormatLocale } from '../../locale/formatting/useFormatLocale';
import { isThaiLocale } from '../../locale/localeUtils';
import { getDayPeriodOrder, getWeekNumber, getYearOfWeek, getYearOrder } from './DateUtil';
import { calculateHourDurationValue, calculateMillisecondsDurationValue, calculateMinuteDurationValue, calculateSecondDurationValue, getFractionalSecondSeparator } from './Pickers/TimePicker';
import bundle from './NovaComponents-DatePicker-gui-icu.properties';
const useTextLocalizer = createUseTextLocalizer(bundle);
export function useDefaultDateTimeFormat(type) {
  const localize = useTextLocalizer();
  let lang = useFormatLocale();
  // Set the numberingSystem to ensure the date/time displays consistently across browsers
  lang = `${lang}-u-nu-latn`;
  return useMemo(() => {
    const opts = typeof type === 'string' ? {
      type
    } : type;
    switch (opts.type) {
      case 'dayOnly':
        return bindFormatToParts(new Intl.DateTimeFormat(lang, {
          day: 'numeric'
        }), lang, 'day');
      case 'monthOnly':
        return bindFormatToParts(new Intl.DateTimeFormat(lang, {
          month: 'numeric'
        }), lang, 'month');
      case 'yearOnly':
        return bindFormatToParts(new Intl.DateTimeFormat(lang, {
          year: 'numeric'
        }), lang, 'year');
      case 'date':
        return bindFormat(new Intl.DateTimeFormat(lang, {
          dateStyle: 'medium'
        }), lang);
      case 'month':
        return bindFormat(new Intl.DateTimeFormat(lang, {
          month: 'short',
          year: 'numeric'
        }), lang);
      case 'datetime':
        {
          const format = new Intl.DateTimeFormat(lang, opts.showSeconds === false || opts.showHours === false ? {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: opts.showHours === false ? undefined : 'numeric',
            minute: 'numeric',
            second: opts.showSeconds === false ? undefined : 'numeric'
          } : {
            dateStyle: 'medium',
            timeStyle: 'medium'
          });
          return opts.showMilliseconds ? opts.showDayPeriod === false ? bindTimeFormatWithMSWithoutDayPeriod(format, lang, opts.show24HourClock) : bindTimeFormatWithMS(format, lang, opts.show24HourClock) : opts.showDayPeriod === false ? bindTimeFormatWithoutDayPeriod(format, lang, opts.show24HourClock, opts.showHours, opts.showMinutes, opts.roundSeconds) : opts.showMinutes === false && opts.showSeconds ? bindFormatWithoutMinutesButWithSeconds(format, lang, opts.show24HourClock, opts.showHours, opts.roundSeconds) : opts.showMinutes === false && opts.showSeconds === false ? bindFormatWithoutMinutesAndSeconds(format, lang, opts.show24HourClock, opts.showHours) : bindFormat(format, lang, opts.show24HourClock, opts.roundSeconds);
        }
      case 'time':
        {
          const format = new Intl.DateTimeFormat(lang, {
            hour: opts.showHours === false ? undefined : 'numeric',
            minute: 'numeric',
            second: opts.showSeconds === false ? undefined : 'numeric'
          });
          return opts.showMilliseconds ? opts.showDayPeriod === false ? bindTimeFormatWithMSWithoutDayPeriod(format, lang, opts.show24HourClock) : opts.showHoursDuration || opts.showMinutesDuration ? bindTimeFormatWithDurations(format, lang, opts.showHoursDuration, opts.showMinutesDuration, opts.showMilliseconds) : bindTimeFormatWithMS(format, lang, opts.show24HourClock) : opts.showHoursDuration || opts.showMinutesDuration ? bindTimeFormatWithDurations(format, lang, opts.showHoursDuration, opts.showMinutesDuration) : opts.showDayPeriod === false ? bindTimeFormatWithoutDayPeriod(format, lang, opts.show24HourClock, opts.showHours, opts.showMinutes, opts.roundSeconds) : opts.showMinutes === false && opts.showSeconds ? bindFormatWithoutMinutesButWithSeconds(format, lang, opts.show24HourClock, opts.showHours, opts.roundSeconds) : opts.showMinutes === false && opts.showSeconds === false ? bindFormatWithoutMinutesAndSeconds(format, lang, opts.show24HourClock, opts.showHours) : bindFormat(format, lang, opts.show24HourClock, opts.roundSeconds);
        }
      case 'quarter':
        {
          const yearOrder = getYearOrder(lang);
          const yearFormat = new Intl.DateTimeFormat(lang, {
            year: 'numeric'
          });
          return function quarterDefaultFormat(value) {
            const quarter = String(Math.floor(value.getMonth() / 3) + 1);
            const year = yearFormat.format(value);
            return yearOrder === 'after' ? localize('quarter.msg', {
              quarter,
              year
            }) : localize('quarterYearFirst.msg', {
              year,
              quarter
            });
          };
        }
      case 'week':
        {
          const yearOrder = getYearOrder(lang);
          const yearFormat = new Intl.DateTimeFormat(lang, {
            year: 'numeric'
          });
          const weekDefaultFormat = value => {
            const week = String(getWeekNumber(value));
            const yearValue = new Date(value);
            /*
              getYearOfWeek uses the locale in order to convert years to thai years.  Since
              Intl.DateTimeFormat will also covert years to thai years, getYearOfWeek should
              not use the locale in this instance.
            */
            yearValue.setFullYear(getYearOfWeek(yearValue, 'none'));
            const year = yearFormat.format(yearValue);
            return yearOrder === 'after' ? localize('week.msg', {
              week,
              year
            }) : localize('weekYearFirst.msg', {
              year,
              week
            });
          };
          return weekDefaultFormat;
        }
      default:
        assertNever(opts);
    }
  }, [lang, type, localize]);
}

/** This is based on a future browser spec for Intl.DateTimeFormatPart. */

const bindFormat = (formatter, lang, show24HourClock, roundSeconds) => value => {
  const isSafari = getBrowserName() === BrowserName.Safari;
  if (isSafari && value.getFullYear() < 1883) value = getAdjustedDateForSafari(value);
  const thaiDateOffset = new Date(1582, 9, 15, 0, 0, 0);
  if (isThaiLocale(lang) && value.getTime() < thaiDateOffset.getTime()) value = getAdjustedDateForThai(value);
  if (show24HourClock && !is24HourLocale(lang)) {
    return convertTo24hour(formatter, value);
  }
  if (roundSeconds) {
    return roundSecondsValue(formatter, value);
  }
  return formatter.format(value);
};
const bindFormatToParts = (formatter, lang, type) => value => {
  const isSafari = getBrowserName() === BrowserName.Safari;
  if (isSafari && value.getFullYear() < 1883) value = getAdjustedDateForSafari(value);
  const thaiDateOffset = new Date(1582, 9, 15, 0, 0, 0);
  if (isThaiLocale(lang) && value.getTime() < thaiDateOffset.getTime()) value = getAdjustedDateForThai(value);
  return formatter.formatToParts(value).find(part => part.type === type)?.value ?? formatter.format(value);
};
const bindTimeFormatWithMS = (formatter, lang, show24HourClock) => value => {
  const isSafari = getBrowserName() === BrowserName.Safari;
  if (isSafari && value.getFullYear() < 1883) value = getAdjustedDateForSafari(value);
  const thaiDateOffset = new Date(1582, 9, 15, 0, 0, 0);
  if (isThaiLocale(lang) && value.getTime() < thaiDateOffset.getTime()) value = getAdjustedDateForThai(value);
  const parts = formatter.formatToParts(value);
  const indexOfSecondsPart = parts.findIndex(({
    type
  }) => type === 'second');
  if (indexOfSecondsPart !== -1 && !parts.find(({
    type
  }) => type === 'fractionalSecond')) {
    parts.splice(indexOfSecondsPart + 1, 0, {
      type: 'literal',
      value: getFractionalSecondSeparator(lang)
    }, {
      type: 'fractionalSecond',
      value: String(value.getMilliseconds()).padStart(3, '0')
    });
  }
  if (show24HourClock && !is24HourLocale(lang)) {
    return convertTo24hour(formatter, value);
  }
  return parts.map(part => part.value).join('');
};
const bindTimeFormatWithoutDayPeriod = (formatter, lang, show24HourClock, showHours, showMinutes, roundSeconds) => value => {
  const isSafari = getBrowserName() === BrowserName.Safari;
  if (isSafari && value.getFullYear() < 1883) value = getAdjustedDateForSafari(value);
  const thaiDateOffset = new Date(1582, 9, 15, 0, 0, 0);
  if (isThaiLocale(lang) && value.getTime() < thaiDateOffset.getTime()) value = getAdjustedDateForThai(value);
  const parts = formatter.formatToParts(value);
  if (showMinutes === false) {
    const indexOfMinutes = parts.findIndex(({
      type
    }) => type === 'minute');
    if (indexOfMinutes !== -1) {
      const minutes = parts.find(item => item.type === 'minute');
      // remove seconds and literal before seconds
      parts.splice(indexOfMinutes - 1, showHours === false ? minutes?.value.length + 1 : minutes?.value.length);
    }
  }
  if (roundSeconds) {
    const indexOfSeconds = parts.findIndex(({
      type
    }) => type === 'second');
    if (indexOfSeconds !== -1) {
      if (value.getMilliseconds() >= 500) parts[indexOfSeconds].value = (value.getSeconds() + 1).toString();
    }
  }
  const indexOfDayPeriod = parts.findIndex(({
    type
  }) => type === 'dayPeriod');
  if (indexOfDayPeriod !== -1) {
    const dayPeriod = parts.find(item => item.type === 'dayPeriod');
    parts.splice(indexOfDayPeriod, dayPeriod?.value.length);
  }
  if (show24HourClock && !is24HourLocale(lang)) {
    return convertTo24hour(formatter, value, showMinutes === false);
  }
  return parts.map(part => part.value).join('');
};
const bindTimeFormatWithMSWithoutDayPeriod = (formatter, lang, show24HourClock) => value => {
  const isSafari = getBrowserName() === BrowserName.Safari;
  if (isSafari && value.getFullYear() < 1883) value = getAdjustedDateForSafari(value);
  const thaiDateOffset = new Date(1582, 9, 15, 0, 0, 0);
  if (isThaiLocale(lang) && value.getTime() < thaiDateOffset.getTime()) value = getAdjustedDateForThai(value);
  const parts = formatter.formatToParts(value);
  const indexOfSecondsPart = parts.findIndex(({
    type
  }) => type === 'second');
  if (indexOfSecondsPart !== -1 && !parts.find(({
    type
  }) => type === 'fractionalSecond')) {
    parts.splice(indexOfSecondsPart + 1, 0, {
      type: 'literal',
      value: getFractionalSecondSeparator(lang)
    }, {
      type: 'fractionalSecond',
      value: String(value.getMilliseconds()).padStart(3, '0')
    });
  }
  const indexOfDayPeriod = parts.findIndex(({
    type
  }) => type === 'dayPeriod');
  if (indexOfDayPeriod !== -1) {
    const dayPeriod = parts.find(item => item.type === 'dayPeriod');
    parts.splice(indexOfDayPeriod, dayPeriod?.value.length);
  }
  if (show24HourClock && !is24HourLocale(lang)) {
    return convertTo24hour(formatter, value);
  }
  return parts.map(part => part.value).join('');
};
const bindFormatWithoutMinutesButWithSeconds = (formatter, lang, show24HourClock, showHours, roundSeconds) => value => {
  const isSafari = getBrowserName() === BrowserName.Safari;
  if (isSafari && value.getFullYear() < 1883) value = getAdjustedDateForSafari(value);
  const thaiDateOffset = new Date(1582, 9, 15, 0, 0, 0);
  if (isThaiLocale(lang) && value.getTime() < thaiDateOffset.getTime()) value = getAdjustedDateForThai(value);
  const parts = formatter.formatToParts(value);
  if (show24HourClock && !is24HourLocale(lang)) {
    return convertTo24hour(formatter, value, true, false);
  }
  if (showHours === false) {
    const indexOfHours = parts.findIndex(({
      type
    }) => type === 'hour');
    if (indexOfHours !== -1) {
      const hours = parts.find(item => item.type === 'hour');
      parts.splice(indexOfHours, hours?.value.length);
    }
  }
  const indexOfMinutes = parts.findIndex(({
    type
  }) => type === 'minute');
  if (indexOfMinutes !== -1) {
    const minutes = parts.find(item => item.type === 'minute');
    parts.splice(indexOfMinutes, minutes?.value.length);
  }
  if (roundSeconds) {
    const indexOfSeconds = parts.findIndex(({
      type
    }) => type === 'second');
    if (indexOfSeconds !== -1) {
      if (value.getMilliseconds() >= 500) parts[indexOfSeconds].value = (value.getSeconds() + 1).toString();
    }
  }
  return parts.map(part => part.value).join('');
};
const bindFormatWithoutMinutesAndSeconds = (formatter, lang, show24HourClock, showHours) => value => {
  const isSafari = getBrowserName() === BrowserName.Safari;
  if (isSafari && value.getFullYear() < 1883) value = getAdjustedDateForSafari(value);
  const thaiDateOffset = new Date(1582, 9, 15, 0, 0, 0);
  if (isThaiLocale(lang) && value.getTime() < thaiDateOffset.getTime()) value = getAdjustedDateForThai(value);
  const parts = formatter.formatToParts(value);
  if (show24HourClock && !is24HourLocale(lang)) {
    return convertTo24hour(formatter, value, true, true);
  }
  if (showHours === false) {
    const indexOfHours = parts.findIndex(({
      type
    }) => type === 'hour');
    if (indexOfHours !== -1) {
      const hours = parts.find(item => item.type === 'hour');
      parts.splice(indexOfHours, hours?.value.length);
    }
  }
  const indexOfMinutes = parts.findIndex(({
    type
  }) => type === 'minute');
  if (indexOfMinutes !== -1) {
    const minutes = parts.find(item => item.type === 'minute');
    // remove seconds and literal before seconds
    parts.splice(indexOfMinutes - 1, showHours === false ? minutes?.value.length + 1 : minutes?.value.length);
  }
  const indexOfSeconds = parts.findIndex(({
    type
  }) => type === 'second');
  if (indexOfSeconds !== -1) {
    const seconds = parts.find(item => item.type === 'second');
    parts.splice(indexOfSeconds, seconds?.value.length);
  }
  return parts.map(part => part.value).join('');
};
const bindTimeFormatWithDurations = (formatter, lang, showHoursDuration, showMinutesDuration, showMilliseconds) => value => {
  const isSafari = getBrowserName() === BrowserName.Safari;
  if (isSafari && value.getFullYear() < 1883) value = getAdjustedDateForSafari(value);
  const thaiDateOffset = new Date(1582, 9, 15, 0, 0, 0);
  if (isThaiLocale(lang) && value.getTime() < thaiDateOffset.getTime()) value = getAdjustedDateForThai(value);
  const parts = formatter.formatToParts(value);
  const dayPeriodIndex = parts.findIndex(({
    type
  }) => type === 'dayPeriod');
  if (dayPeriodIndex !== -1) parts.splice(dayPeriodIndex, 1);
  const isNegDurations = isNegativeDurations(value);
  const partValues = parts.map(p => p.value);
  const indexOfHoursPart = parts.findIndex(({
    type
  }) => type === 'hour');
  if (indexOfHoursPart !== -1) {
    const hoursValue = calculateHourDurationValue(value);
    partValues[indexOfHoursPart] = `${isNegDurations && hoursValue === 0 ? '-' : ''}${hoursValue.toString()}`;
  }
  const indexOfMinutesPart = parts.findIndex(({
    type
  }) => type === 'minute');
  if (indexOfMinutesPart !== -1) {
    const minutesValue = calculateMinuteDurationValue(value, showHoursDuration, showMinutesDuration);
    partValues[indexOfMinutesPart] = String(minutesValue.toString()).padStart(2, isNegDurations && !showHoursDuration && minutesValue === 0 ? '-' : '0');
  }
  const indexOfSecondsPart = parts.findIndex(({
    type
  }) => type === 'second');
  if (indexOfSecondsPart !== -1) {
    const secondsValue = calculateSecondDurationValue(value, showHoursDuration, showMinutesDuration);
    partValues[indexOfSecondsPart] = String(secondsValue.toString()).padStart(2, '0');
    if (showMilliseconds) {
      const millisecondsValue = calculateMillisecondsDurationValue(value, showHoursDuration, showMinutesDuration);
      const indexOfFractionalSecond = parts.findIndex(({
        type
      }) => type === 'fractionalSecond');
      if (indexOfFractionalSecond !== -1) {
        partValues.splice(indexOfFractionalSecond, indexOfSecondsPart + 1);
      }
      partValues.push(getFractionalSecondSeparator(lang));
      partValues.push(String(millisecondsValue.toString()).padStart(3, '0'));
    }
  }
  return partValues.join('').trim().replace(/\s+/g, '');
};
const getAdjustedDateForSafari = value => {
  // NOTE: There is an issue with the implementation of Intl.DateTimeFormat in Safari.
  // In Safari format() adds 3 minutes 58 seconds to the time for years before 1883.  Need to subtract it out.
  const tmpValue = new Date(value.getTime());
  tmpValue.setMinutes(tmpValue.getMinutes() - 3);
  tmpValue.setSeconds(tmpValue.getSeconds() - 58);
  return tmpValue;
};
const getAdjustedDateForThai = value => {
  // NOTE: There is an issue with the implementation of Intl.DateTimeFormat for Thai dates before 10/15/1582 Gregorian.
  // Need to add 10 days to the date for it to produce the correct date in format.
  const tmpValue = new Date(value.getTime());
  tmpValue.setDate(tmpValue.getDate() + 10);
  return tmpValue;
};
const is24HourLocale = lang => {
  const dayPeriodOrder = getDayPeriodOrder(lang);
  return dayPeriodOrder === 'none';
};
const isNegativeDurations = date => {
  let negativeDurations = false;
  const defaultDate = new Date(1970, 0, 1, 0, 0, 0, 0);
  const dateDiff = date.valueOf() - defaultDate.valueOf();
  if (dateDiff < 0) {
    negativeDurations = true;
  }
  return negativeDurations;
};
const convertTo24hour = (formatter, value, withoutMinutes, withoutSeconds) => {
  const parts = formatter.formatToParts(value);
  let minutesRemoved = false;
  let secondsRemoved = false;
  const indexOfHour = parts.findIndex(({
    type
  }) => type === 'hour');
  const indexOfMinutes = parts.findIndex(({
    type
  }) => type === 'minute');
  const indexOfSeconds = parts.findIndex(({
    type
  }) => type === 'second');
  if (withoutMinutes) {
    if (indexOfMinutes !== -1) {
      const minutes = parts.find(item => item.type === 'minute');
      parts.splice(indexOfHour !== -1 ? indexOfMinutes - 1 : indexOfMinutes, minutes?.value.length);
      minutesRemoved = true;
    }
  }
  if (withoutSeconds) {
    if (indexOfSeconds !== -1) {
      const seconds = parts.find(item => item.type === 'second');
      parts.splice(indexOfSeconds, seconds?.value.length);
      secondsRemoved = true;
    }
  }
  const indexOfDayPeriod = parts.findIndex(({
    type
  }) => type === 'dayPeriod');
  const dayPeriod = parts.find(item => item.type === 'dayPeriod');
  if (indexOfHour !== -1) {
    parts[indexOfHour].value = value.getHours().toString();
    if (parts[indexOfHour + 1].value === '') {
      parts[indexOfHour + 1].value = ':';
    }
  }
  if (indexOfDayPeriod !== -1) {
    parts.splice(indexOfDayPeriod, dayPeriod?.value.length);
  }
  if (indexOfHour === -1 && (indexOfMinutes === -1 || minutesRemoved) && (indexOfSeconds === -1 || secondsRemoved)) {
    // Need to remove comma at end of date
    parts.splice(parts.length - 1);
  }
  return parts.map(part => part.value).join('').trim();
};
const roundSecondsValue = (formatter, value) => {
  const parts = formatter.formatToParts(value);
  const indexOfSeconds = parts.findIndex(({
    type
  }) => type === 'second');
  if (indexOfSeconds !== -1) {
    if (value.getMilliseconds() >= 500) parts[indexOfSeconds].value = (value.getSeconds() + 1).toString();
  }
  return parts.map(part => part.value).join('').trim();
};
//# sourceMappingURL=useDefaultDateTimeFormat.js.map